import { useEffect, useMemo, useState } from "react";
import type {
  CustomOrder,
  Profile,
  ProfileLinkConfig,
  WorkplaceItem,
} from "../../../../../client";
import { BulletSectionTitle, Checkbox } from "../../../../../components";
import { Workplace } from "./Workplace";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";
import { useStatsigClient } from "../../../../../hooks";
import { useThemeColors } from "../../../../../contexts";

interface Props {
  profileData?: Profile;
  linkConfigData: ProfileLinkConfig;
  handleUpdateLink: (data: Partial<ProfileLinkConfig>) => void;
  isDefaultLead: boolean;
}

interface SortableWorkplaceProps {
  workplace: WorkplaceItem;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  showSkills?: boolean;
  showDescription?: boolean;
  isEditiable: boolean;
  onWorkplaceEdit: (description: string) => void;
  isEdited: boolean;
  isDragEnabled: boolean;
}

const SortableWorkplace = ({
  workplace,
  checked,
  onCheckedChange,
  showSkills,
  showDescription,
  isEditiable,
  onWorkplaceEdit,
  isEdited,
  isDragEnabled,
}: SortableWorkplaceProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: workplace.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="touch-none">
      <div
        {...attributes}
        {...listeners}
        className={isDragEnabled ? "cursor-grab" : ""}
      >
        <Workplace
          key={workplace.id}
          isEditable={false}
          checked={checked}
          onCheckedChange={onCheckedChange}
          name={workplace.name}
          skillTags={workplace.skillTags}
          role={workplace.role}
          description={workplace.description}
          startDate={workplace.startDate}
          endDate={workplace.endDate}
          showSkills={showSkills}
          showDescription={showDescription}
          isEditiable={isEditiable}
          onWorkplaceEdit={onWorkplaceEdit}
          isEdited={isEdited}
          isDefaultLead={false}
        />
      </div>
    </div>
  );
};

export const LinkCreatorHighlightedProjects = ({
  profileData,
  linkConfigData,
  handleUpdateLink,
  isDefaultLead,
}: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeWorkplace, setActiveWorkplace] = useState<WorkplaceItem | null>(
    null,
  );
  const [localWorkplaces, setLocalWorkplaces] = useState<WorkplaceItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const statsigClient = useStatsigClient();
  const isDragEnabled =
    statsigClient?.checkGate("enable_workplace_drag_and_drop") ?? false;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const { secondary } = useThemeColors();

  const handleCheckedChange = (checked: boolean, workplaceTitle: string) => {
    if (!linkConfigData.highlightedProjects) {
      return;
    }

    linkConfigData.highlightedProjects.filter(
      (hp) => hp.title === workplaceTitle,
    )[0].included = checked;

    handleUpdateLink({
      highlightedProjects: linkConfigData.highlightedProjects,
    });
  };

  const handleUpdatedWorkplace = (workplace: WorkplaceItem) => {
    if (!linkConfigData) {
      return;
    }

    if (!linkConfigData.editedWorkplaces) {
      linkConfigData.editedWorkplaces = [];
    }

    var currentEditedWorkplaces = [...linkConfigData.editedWorkplaces];

    var found = false;
    for (var i = 0; i < currentEditedWorkplaces.length; i++) {
      if (currentEditedWorkplaces[i].id === workplace.id) {
        currentEditedWorkplaces[i] = workplace;
        found = true;
      }
    }

    if (!found) {
      currentEditedWorkplaces.push(workplace);
    }

    handleUpdateLink({ editedWorkplaces: currentEditedWorkplaces });
  };

  const getWorkplaceName = (workplaceId: string) => {
    if (
      profileData &&
      profileData.workplaces &&
      profileData.workplaces.filter((wp) => wp.id === workplaceId).length > 0
    ) {
      return profileData.workplaces.filter((wp) => wp.id === workplaceId)[0]
        .name;
    }
    return "";
  };

  const getWorkplaceNameFromProject = (projectId: string) => {
    if (
      profileData &&
      profileData.side_projects_section &&
      profileData.side_projects_section.side_projects.filter(
        (p) => p.id === projectId,
      ).length > 0
    ) {
      let project = profileData.side_projects_section.side_projects.filter(
        (p) => p.id === projectId,
      )[0];

      return getWorkplaceName(project.workplaceId ?? "");
    }
    return "";
  };
  return (
    linkConfigData?.highlightedProjects &&
    linkConfigData.highlightedProjects.length > 0 && (
      <div className="flex flex-col gap-4">
        <div style={{ "--text-color": secondary } as React.CSSProperties}>
          <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
            Highlighted projects
          </BulletSectionTitle>
        </div>

        <div className="flex flex-col gap-6">
          {isDefaultLead ? (
            <div className="flex flex-row items-center gap-4 w-full h-full">
              <Checkbox
                checked={
                  linkConfigData.includeHighlightedProjectsAsDefault ?? false
                }
                onCheckedChange={(checked: boolean) => {
                  handleUpdateLink({
                    includeHighlightedProjectsAsDefault: checked ?? false,
                  });
                }}
              />
              <div className="">
                <p className="font-semibold">
                  Include Highlighted Projects as default
                </p>
                <p className="text-xs">
                  Enrich your Profile Versions with highlighted projects.
                  Automatically generated based on the the profiles experience
                  and the details of the assignment.
                </p>
              </div>
            </div>
          ) : (
            linkConfigData.highlightedProjects &&
            linkConfigData.highlightedProjects.map(
              (highlightedProject, index) => (
                <Workplace
                  key={highlightedProject.title}
                  isEditable={false}
                  checked={highlightedProject.included}
                  onCheckedChange={(checked) => {
                    handleCheckedChange(
                      checked,
                      highlightedProject.title ?? "",
                    );
                  }}
                  name={
                    highlightedProject.workplaceId
                      ? getWorkplaceName(highlightedProject.workplaceId ?? "")
                      : highlightedProject.projectId
                        ? getWorkplaceNameFromProject(
                            highlightedProject.projectId,
                          )
                        : ""
                  }
                  skillTags={highlightedProject.skillTags ?? []}
                  role={highlightedProject.title ?? ""}
                  description={highlightedProject.summary}
                  isProject={true}
                  showSkills={
                    linkConfigData.highlightedProjectsConfig?.showSkills ?? true
                  }
                  showSkillIcons={
                    linkConfigData.highlightedProjectsConfig?.showIcons ?? true
                  }
                  showDescription={true}
                  isEditiable={true}
                  onWorkplaceEdit={(description: string) => {
                    if (
                      linkConfigData.highlightedProjects &&
                      linkConfigData.highlightedProjects[index]
                    ) {
                      let hps = [...linkConfigData.highlightedProjects];
                      hps[index].summary = description;
                      handleUpdateLink({ highlightedProjects: hps });
                    }
                  }}
                  isEdited={false}
                  isDefaultLead={isDefaultLead}
                />
              ),
            )
          )}
        </div>
      </div>
    )
  );
};
